import { useState, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { agencyService, ReferredBrandsTableResponse, ReferredBrandsSummaryResponse, ReferredBrand } from '@/services/agency.service';
import { Search, TrendingUp, Users, CreditCard, Plus, MessageSquare } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { RequestBrandModal } from '@/components/agency/RequestBrandModal';
import { AgencyBrandRequests } from '@/components/agency/AgencyBrandRequests';
import { DataTable, DataTablePagination } from '@/components/ui/data-table';
import { UnderlineTabs } from '@/components/ui/underline-tabs';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const columns: ColumnDef<ReferredBrand>[] = [
  {
    accessorKey: 'companyName',
    header: 'Brand',
    cell: ({ row }) => (
      <span className="font-medium text-foreground">{row.original.companyName}</span>
    ),
    meta: { width: 'w-[22%]' },
  },
  {
    accessorKey: 'joinedDate',
    header: 'Joined',
    cell: ({ row }) => (
      <span className="text-muted-foreground">{formatDate(row.original.joinedDate)}</span>
    ),
    meta: { width: 'w-[14%]' },
  },
  {
    id: 'niches',
    header: 'Niches',
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-1">
        {row.original.niches.map((niche: string, index: number) => (
          <span
            key={index}
            className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded-full"
          >
            {niche}
          </span>
        ))}
      </div>
    ),
    meta: { width: 'w-[26%]' },
  },
  {
    accessorKey: 'commission',
    header: 'My Commission',
    cell: ({ row }) => (
      <span className="font-medium text-green-600">{formatCurrency(row.original.commission)}</span>
    ),
    meta: { width: 'w-[14%]' },
  },
  {
    accessorKey: 'subscriptionType',
    header: 'Subscription',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{row.original.subscriptionType}</span>
    ),
    meta: { width: 'w-[12%]' },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span
        className={`px-3 py-1 text-xs rounded-full font-medium ${
          row.original.status === 'Active'
            ? 'bg-black text-white'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {row.original.status}
      </span>
    ),
    meta: { width: 'w-[12%]' },
  },
];

export function AgencyBrands() {
  const [brandsData, setBrandsData] = useState<ReferredBrandsTableResponse | null>(null);
  const [summaryData, setSummaryData] = useState<ReferredBrandsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'referred' | 'requests'>('referred');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when searching
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch summary data
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await agencyService.getReferredBrandsSummary();
        setSummaryData(data);
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    };

    fetchSummary();
  }, []);

  // Fetch table data
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        const data = await agencyService.getReferredBrandsTable(currentPage, pageSize, debouncedSearchTerm);
        setBrandsData(data);
      } catch (error) {
        console.error('Error fetching brands:', error);
        toast.error('Failed to load brands. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [currentPage, pageSize, debouncedSearchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRequestSent = () => {
    // Refresh the requests tab if it's active
    if (activeTab === 'requests') {
      // The AgencyBrandRequests component will handle its own refresh
    }
  };

  if (loading && !brandsData) {
    return (
      <div className="space-y-6">
        <div className="border-b border-border pb-4">
          <h1 className="text-2xl font-bold text-foreground">My Brands</h1>
          <p className="text-muted-foreground">Brands you've successfully referred to our platform.</p>
        </div>
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded-lg"></div>
            ))}
          </div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground">My Brands</h1>
        <p className="text-muted-foreground">Manage your brand relationships and requests.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Brands</p>
                <p className="text-2xl font-bold text-foreground">{summaryData?.totalBrands || 0}</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                <Users className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Brands</p>
                <p className="text-2xl font-bold text-foreground">{summaryData?.activeBrands || 0}</p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <TrendingUp className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">My Commission</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(summaryData?.totalCommission || 0)}</p>
              </div>
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <CreditCard className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Actions</p>
                <Button
                  onClick={() => setShowRequestModal(true)}
                  size="sm"
                  className="mt-2 bg-brand-600 hover:bg-brand-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Request Brand
                </Button>
              </div>
              <div className="p-2 rounded-lg bg-brand-100 text-brand-600">
                <MessageSquare className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <UnderlineTabs<'referred' | 'requests'>
        tabs={[
          { value: 'referred', label: 'Referred Brands' },
          { value: 'requests', label: 'Brand Requests' },
        ]}
        value={activeTab}
        onValueChange={setActiveTab}
        accent="brand"
      />

      {/* Tab Content */}
      {activeTab === 'referred' ? (
        <>
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <input
              type="text"
              placeholder="Search my brands..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
          </div>

          {/* Brands Table */}
          <div className="rounded-xl border border-border bg-card shadow-sm">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">My Referred Brands</h2>
            </div>
            <DataTable
              columns={columns}
              data={brandsData?.brands ?? []}
              isLoading={loading}
              emptyText="No brands found matching your search."
            />
            <DataTablePagination
              page={currentPage}
              totalPages={brandsData?.pagination.totalPages ?? 1}
              total={brandsData?.pagination.total ?? 0}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              noun="brands"
            />
          </div>
        </>
      ) : (
        <AgencyBrandRequests />
      )}

      {/* Request Brand Modal */}
      <RequestBrandModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onRequestSent={handleRequestSent}
      />
    </div>
  );
}
